import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AdminInfo } from "@/types/superAdmin";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface DeleteAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  admin: AdminInfo | null;
  onConfirm: (userId: string, deleteAccount: boolean) => void;
  isLoading?: boolean;
}

export function DeleteAdminDialog({ open, onOpenChange, admin, onConfirm, isLoading }: DeleteAdminDialogProps) {
  const { t } = useTranslation(["superadmin", "common"]);
  const [confirmation, setConfirmation] = useState("");
  const [deleteAccount, setDeleteAccount] = useState(false);

  if (!admin) return null;

  const expectedConfirmation = `${admin.first_name} ${admin.last_name}`;
  const isValid = confirmation === expectedConfirmation;

  const handleConfirm = () => {
    if (isValid) {
      onConfirm(admin.user_id, deleteAccount);
      setConfirmation("");
      setDeleteAccount(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            {t("deleteAdmin.title")}
          </DialogTitle>
          <DialogDescription>
            {t("deleteAdmin.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/5">
            <p className="font-medium mb-2">{admin.first_name} {admin.last_name}</p>
            <p className="text-sm text-muted-foreground mb-2">{admin.email}</p>
            <div className="text-sm">
              <p className="font-medium mb-1">{t("deleteAdmin.assignedTenants")} :</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                {admin.tenants.map((tenant) => (
                  <li key={tenant.gym_id}>
                    {tenant.gym_name} - {tenant.city}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-start space-x-2">
              <Checkbox
                id="deleteAccount"
                checked={deleteAccount}
                onCheckedChange={(checked) => setDeleteAccount(checked as boolean)}
              />
              <div className="space-y-1">
                <label
                  htmlFor="deleteAccount"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {t("deleteAdmin.deleteAccountCompletely")}
                </label>
                <p className="text-xs text-muted-foreground">
                  {t("deleteAdmin.uncheckedNote")}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmation">
              {t("deleteAdmin.typeToConfirm")} <span className="font-bold">{expectedConfirmation}</span>
            </Label>
            <Input
              id="confirmation"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder={t("deleteAdmin.fullNamePlaceholder")}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common:cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={!isValid || isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("deleteAdmin.confirmDeletion")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
